/**
 * callDecline — the pure rules behind "the other side declined your call".
 *
 * ── Why this module exists ───────────────────────────────────────────────────
 * Declining an incoming consultation call only stopped the recipient's own
 * ringtone. The caller was left on "Waiting for patient…" until the ring timed
 * out, with no way to tell "they said no" from "nobody was there" — two
 * completely different facts for a clinician.
 *
 * The server now answers a decline with `consultation:declined` on the caller's
 * own room (see backend consultationDecline.js). This module owns the client's
 * truth about that frame and is deliberately pure — no React, no sockets — so
 * every rule is unit-testable without a browser:
 *
 *   - a MALFORMED frame is refused rather than rendered;
 *   - a decline for ANOTHER booking or ANOTHER session is ignored, so a stale
 *     disposition can never end the call that replaced it;
 *   - the reason stays a stable CODE internally, and only ever becomes a
 *     translated sentence at the UI boundary — an internal code is never shown
 *     to a user;
 *   - a decline is NOT a timeout and NOT a cancellation: the caller is told
 *     "Call declined" only when somebody actually declined.
 */

/** Stable disposition codes. Never rendered — always translated by key. */
export const CALL_DECLINE_REASON_CODES = [
  'IN_MEETING',
  'CALL_BACK_SOON',
  'CANNOT_TALK',
  'NOT_AVAILABLE',
  'TECHNICAL',
  'OTHER',
  'NO_REASON',
] as const;

export type CallDeclineReasonCode = (typeof CALL_DECLINE_REASON_CODES)[number];

/** Mirrors the server's cap; the server is still the authority for storage. */
export const DECLINE_NOTE_MAX_LENGTH = 160;

/** A validated `consultation:declined` disposition. */
export interface CallDecline {
  bookingId: number;
  sessionId: number;
  fromUid: string | null;
  fromRole: string | null;
  reasonCode: CallDeclineReasonCode;
  note: string | null;
  declinedAt: string | null;
}

function positiveInt(value: unknown): number | null {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Validate a raw `consultation:declined` payload.
 *
 * Anything that does not identify a real booking + session is refused: an
 * unidentifiable disposition must never be able to end a call screen. An
 * unknown reason code degrades to NO_REASON (an unknown code is not a reason),
 * so the UI can never be handed a code it cannot translate.
 */
export function parseCallDeclined(payload: unknown): CallDecline | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;

  const bookingId = positiveInt(raw.bookingId);
  const sessionId = positiveInt(raw.sessionId);
  if (bookingId === null || sessionId === null) return null;

  const code = typeof raw.reasonCode === 'string' ? raw.reasonCode.trim().toUpperCase() : '';
  const reasonCode = (CALL_DECLINE_REASON_CODES as readonly string[]).includes(code)
    ? (code as CallDeclineReasonCode)
    : 'NO_REASON';

  const rawNote = optionalString(raw.note);
  // Free text is meaningful for OTHER only — same rule the server enforces.
  const note = reasonCode === 'OTHER' && rawNote ? rawNote.slice(0, DECLINE_NOTE_MAX_LENGTH) : null;

  return {
    bookingId,
    sessionId,
    fromUid: optionalString(raw.fromUid),
    fromRole: optionalString(raw.fromRole),
    reasonCode,
    note,
    declinedAt: optionalString(raw.declinedAt),
  };
}

/**
 * Does this disposition belong to the call currently on screen?
 * Scoping by BOTH booking and session is what stops a late decline for an ended
 * session from tearing down a newer call.
 */
export function isDeclineForCall(
  decline: CallDecline | null,
  bookingId: number | null | undefined,
  sessionId: number | null | undefined
): boolean {
  if (!decline) return false;
  if (bookingId == null || sessionId == null) return false;
  return String(decline.bookingId) === String(bookingId)
    && String(decline.sessionId) === String(sessionId);
}

/**
 * How the caller should be told WHY, in natural language.
 *
 *   reason  → a canned sentence, translated with the decliner's role
 *   note    → the recipient's own words (OTHER only), shown as text
 *   none    → decline without a reason; the caller still sees "Call declined"
 */
export type DeclineDetail =
  | { kind: 'reason'; key: string }
  | { kind: 'note'; note: string }
  | { kind: 'none' };

/**
 * Map a decline to its caller-facing explanation.
 * A free-text note WINS over the canned sentence, because it says more.
 */
export function describeDecline(decline: CallDecline): DeclineDetail {
  if (decline.note) return { kind: 'note', note: decline.note };

  switch (decline.reasonCode) {
    case 'IN_MEETING':
      return { kind: 'reason', key: 'callDecline.summary.inMeeting' };
    case 'CALL_BACK_SOON':
      return { kind: 'reason', key: 'callDecline.summary.callBackSoon' };
    case 'CANNOT_TALK':
      return { kind: 'reason', key: 'callDecline.summary.cannotTalk' };
    case 'NOT_AVAILABLE':
      return { kind: 'reason', key: 'callDecline.summary.notAvailable' };
    case 'TECHNICAL':
      return { kind: 'reason', key: 'callDecline.summary.technical' };
    // OTHER without a note, and NO_REASON, say nothing beyond "Call declined".
    default:
      return { kind: 'none' };
  }
}

/**
 * How the CALLER refers to the party that declined.
 * Always a NOUN PHRASE ("The patient"), never a bare role label, because every
 * summary sentence below uses it as the subject of a verb.
 */
export function declineActorKey(fromRole: string | null | undefined): string {
  if (fromRole === 'patient') return 'callDecline.who.patient';
  if (fromRole === 'doctor') return 'callDecline.who.doctor';
  return 'callDecline.who.unknown';
}

/**
 * The reason codes a recipient may pick, in display order, as i18n keys.
 * `NO_REASON` is represented by the explicit "decline without a reason" action
 * rather than as a radio option, so it is not part of this list.
 *
 * `CANNOT_TALK` is deliberately absent from this list (it is folded into "I'm
 * unavailable right now") but is still PARSED: an in-flight decline carrying an
 * older code must keep working and must still be explained to the caller, not
 * silently degraded to "no reason".
 */
export const DECLINE_REASON_OPTION_KEYS: ReadonlyArray<{ code: CallDeclineReasonCode; key: string }> = [
  { code: 'IN_MEETING', key: 'callDecline.reason.inMeeting' },
  { code: 'NOT_AVAILABLE', key: 'callDecline.reason.notAvailable' },
  { code: 'CALL_BACK_SOON', key: 'callDecline.reason.callBackSoon' },
  { code: 'TECHNICAL', key: 'callDecline.reason.technical' },
  { code: 'OTHER', key: 'callDecline.reason.other' },
];

/**
 * The chips offered next to "Other" — everything except the free-text choice,
 * which needs its own one-tap action ("Other" opens the note field).
 */
export const DECLINE_QUICK_REASON_KEYS: ReadonlyArray<{ code: CallDeclineReasonCode; key: string }> =
  DECLINE_REASON_OPTION_KEYS.filter(option => option.code !== 'OTHER');
